import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Outlet, NavLink } from 'react-router-dom';
import { FiMenu, FiX, FiLogOut, FiHome, FiFilm, FiStar, FiUsers, FiChevronLeft } from 'react-icons/fi';
import AdminLogin from './AdminLogin';
import DirectorsPanel from './DirectorsPanel';
import VideosPanel from './VideosPanel';
import FeaturedSpotsPanel from './FeaturedSpotsPanel';
import Home from './Home';
import DirectorPage from './pages/DirectorPage';
import AdminUploadTestPage from './pages/AdminUploadTestPage';
import AdminNewsPage from './pages/AdminNewsPage';
import AdminNewsForm from './pages/AdminNewsForm'; // Import AdminNewsForm // Import AdminNewsPage // Import the new test page // Assuming it will be in a 'pages' subfolder
import AboutPage from './pages/AboutPage'; // Import the new AboutPage
import EditAboutPage from './pages/EditAboutPage'; // Import for admin About Page editing
import ContactPage from './pages/ContactPage'; // Import the new ContactPage
import ContactAdminPage from './pages/ContactAdminPage'; // Import for admin Contact Page editing

const NAV_ITEMS = [
  { id: 'videos', label: 'Videos', path: '/admin/videos' },
  { id: 'directors', label: 'Directors', path: '/admin/directors' },
  { id: 'featured', label: 'Featured Spots', path: '/admin/featured' },
  { id: 'news', label: 'News', path: '/admin/news' }, // Added News link
  { id: 'about', label: 'About Page', path: '/admin/about' }, // Added About Page link
  { id: 'contact', label: 'Contact Page', path: '/admin/contact' }
];

function AdminDashboard({ token, setToken }) {

  const location = useLocation();
  const navigate = useNavigate();

  // Redirect to videos page by default
  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      navigate('/admin/videos', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('jwt_token');
    navigate('/admin-login');
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      {/* Top Navigation */}
      <header className="bg-bg-secondary border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src="/content/images/thedevilyouknow.tv/logo.png" 
                alt="TDYK Logo" 
                className="h-10 w-auto"
              />
            </div>
            
            {/* Navigation */}
            <nav className="ml-auto">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {NAV_ITEMS.map((item) => (
                    <NavLink 
                      key={item.id} 
                      to={item.path} 
                      className={({ isActive }) => 
                        `flex items-center justify-center h-12 min-w-[120px] px-4 py-2 text-sm font-medium text-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isActive 
                            ? 'bg-gray-700 text-white hover:bg-gray-600 focus:ring-gray-500' 
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white focus:ring-gray-500'
                        }`
                      }
                    >
                      <span className="leading-tight">{item.label}</span>
                    </NavLink>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="h-12 min-w-[100px] px-4 py-2 ml-2 text-sm font-medium text-center text-white transition-colors duration-200 bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="bg-bg-secondary p-6">
          <Outlet context={{ token }} />
        </div>
      </main>

      {/* Footer with Logout */}
      <footer className="border-t border-border mt-8 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <button
            onClick={handleLogout}
            className="text-sm text-text-secondary hover:text-text-primary flex items-center"
          >
            <FiLogOut className="mr-1" size={16} />
            Logout
          </button>
        </div>
      </footer>
    </div>
  );
}

// Dashboard Page
function DashboardPage() {
  const stats = [
    {
      title: 'Total Directors',
      value: '24',
      change: '+5%',
      icon: <FiUsers className="text-accent" size={24} />,
      trend: 'up'
    },
    {
      title: 'Total Videos',
      value: '156',
      change: '+12%',
      icon: <FiFilm className="text-accent" size={24} />,
      trend: 'up'
    },
    {
      title: 'Featured Spots',
      value: '8',
      change: '-2%',
      icon: <FiStar className="text-accent" size={24} />,
      trend: 'down'
    },
    {
      title: 'Active Users',
      value: '3',
      change: '0%',
      icon: <FiUsers className="text-accent" size={24} />,
      trend: 'neutral'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      <div className="bg-bg-secondary rounded-xl p-6 border border-border shadow-sm">
        <h2 className="text-2xl font-semibold mb-2">Welcome to TDYK Admin</h2>
        <p className="text-text-secondary">
          Manage your directors, videos, and featured spots from this admin panel.
        </p>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <StatCard 
            key={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            trend={stat.trend}
            icon={stat.icon}
          />
        ))}
      </div>

      {/* Recent Activity Section */}
      <div className="bg-bg-secondary rounded-xl border border-border p-6">
        <h3 className="text-lg font-medium mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex items-center p-3 rounded-lg bg-bg-primary/30 border border-border">
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center text-accent mr-3">
                <FiUsers size={18} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">New director added</p>
                <p className="text-xs text-text-secondary">2 hours ago</p>
              </div>
              <span className="text-xs px-2 py-1 bg-bg-tertiary/50 rounded-md">
                View
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, change, icon, trend = 'neutral' }) {
  const trendColors = {
    up: 'text-green-500',
    down: 'text-red-500',
    neutral: 'text-text-secondary'
  };

  const trendIcons = {
    up: '↑',
    down: '↓',
    neutral: '→'
  };

  return (
    <div className="bg-bg-secondary rounded-xl p-5 border border-border hover:border-accent/20 transition-colors">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-text-secondary mb-1">{title}</p>
          <p className="text-2xl font-semibold mb-2">{value}</p>
          <div className={`text-xs flex items-center ${trendColors[trend]}`}>
            <span className="mr-1">{change} from last month</span>
            <span>{trendIcons[trend]}</span>
          </div>
        </div>
        <div className="p-2.5 rounded-lg bg-bg-tertiary/50 text-accent">
          {icon}
        </div>
      </div>
    </div>
  );
}

function App() {
  const [token, setTokenState] = useState(() => localStorage.getItem('jwt_token') || null);

  const setToken = (newToken, username) => {
    setTokenState(newToken);
    if (newToken) {
      localStorage.setItem('jwt_token', newToken);
      localStorage.setItem('username', username || 'Admin');
    } else {
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('username');
    }
  };

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/directors/:directorId" element={<DirectorPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Admin Login Route */}
        <Route 
          path="/admin-login" 
          element={!token ? <AdminLogin onLogin={setToken} /> : <Navigate to="/admin" replace />}
        />

        {/* Protected Admin Routes */}
        <Route 
          path="/admin" 
          element={token ? <AdminDashboard token={token} setToken={setToken} /> : <Navigate to="/admin-login" replace />}
        >
          <Route index element={<Navigate to="videos" replace />} /> {/* Default to videos panel */}
          <Route path="directors" element={<DirectorsPanel />} />
          <Route path="videos" element={<VideosPanel />} />
          <Route path="featured" element={<FeaturedSpotsPanel />} />
          <Route path="upload-test" element={<AdminUploadTestPage />} /> {/* New test page route */}
          <Route path="news" element={<Outlet />}>
            <Route index element={<AdminNewsPage token={token} />} />
            <Route path="new" element={<AdminNewsForm token={token} />} />
            <Route path="edit/:articleId" element={<AdminNewsForm token={token} />} />
          </Route> {/* Add route for AdminNewsPage */}
          <Route path="about" element={<EditAboutPage token={token} />} /> {/* Route for Edit About Page */}
          <Route path="contact" element={<ContactAdminPage token={token} />} /> {/* Route for Edit Contact Page */}
        </Route>
        
        {/* Fallback to home for any other unmatched routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
